"use client";

// hooks
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { useModal } from "@/contexts/ModalContext";
import { useMemo, useRef, useEffect, useState } from "react";

// components
import Modal from "@/components/ui/Modal";
import GoogleMapCard from "@/components/featured/bus/cards/GoogleMapCard";
import HotelSearchMapView from "@/components/pages/hotel/search/HotelSearchMapView";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";

// utils
import clsx from "clsx";
import { getCdnImagePath } from "@/utils/bus/getCdnImagePath";

export default function MapViewModal({
  containerClassName,
  buttonClassName,
  buttonTextClassName,
  translationNamespace = "common",
  mapSrc,
  mapTitle = "Google Map",
  /** Otel arama haritası: verildiğinde interaktif harita (mapsearch API, marker cluster) açılır; yoksa iframe ile statik harita. */
  mapConfig,
  modalMapFullHeight = false,
  /** Harita açılmadan önce tetiklenir (örn. hotel_map_view analytics). mapConfig yokken (sadece mapSrc) kullanılır. */
  onMapViewOpen,
}) {
  const t = useTranslationDetector(translationNamespace);
  const modalIdRef = useRef(null);
  const modalId = useMemo(() => {
    if (!modalIdRef.current) {
      modalIdRef.current = `map-view-modal-${Math.random()
        .toString(36)
        .slice(2, 11)}`;
    }
    return modalIdRef.current;
  }, []);

  const mapModal = useModal(modalId);
  const [iframeSrc, setIframeSrc] = useState(null);
  const [useInteractiveMap, setUseInteractiveMap] = useState(false);

  // Modal kapandığında iframe src ve interaktif harita flag'ini temizle
  useEffect(() => {
    if (!mapModal.isOpen) {
      setIframeSrc(null);
      setUseInteractiveMap(false);
    }
  }, [mapModal.isOpen]);

  const handleButtonClick = () => {
    (mapConfig?.onMapViewOpen ?? onMapViewOpen)?.();
    if (mapConfig) {
      setUseInteractiveMap(true);
      mapModal.open({
        title: "",
        showCloseButton: false,
        size: "full",
        isBottomSheet: true,
        isFullScreen: true,
        contentClassName: "!flex-1 !min-h-0 !overflow-hidden !p-0 flex flex-col",
      });
    } else if (mapSrc) {
      setIframeSrc(mapSrc);
      mapModal.open({
        title: "",
        showCloseButton: false,
        size: "full",
        isBottomSheet: true,
        isFullScreen: true,
        contentClassName: "!flex-1 !min-h-0 !overflow-hidden !p-0",
      });
    }
  };

  const canOpen = Boolean(mapConfig || mapSrc);

  return (
    <>
      <div className={clsx("w-full h-[180px] md:h-[230px] border border-primary-weak rounded-lg overflow-hidden relative flex justify-center items-center", containerClassName)}>
        <picture className="w-full h-full absolute top-0 left-0">
          <source media="(min-width: 1024px)" srcSet={getCdnImagePath("/saka/hotel/map-hotel-list.png")} />
          <img src={getCdnImagePath("/saka/common/mobile-map.png")} alt={t("mapViewModalButtonText")} className="w-full h-full object-cover object-center" loading="lazy" />
        </picture>
        <button
          type="button"
          onClick={handleButtonClick}
          disabled={!canOpen}
          className={clsx("bg-primary shadow-medium hover:bg-primary/90 hover:shadow-soft flex max-w-[200px] h-[50px] w-full cursor-pointer items-center justify-center gap-2 rounded-lg p-4 text-lg font-semibold text-white transition-all duration-200 ease-in-out z-10 relative disabled:opacity-50 disabled:cursor-not-allowed", buttonClassName)}
        >
          <span className={clsx("whitespace-nowrap", buttonTextClassName)}>
            {t("mapViewModalButtonText")}
          </span>
        </button>
      </div>

      <Modal
        contentClassName={useInteractiveMap ? "!flex-1 !min-h-0 !overflow-hidden !p-0 flex flex-col" : "!h-[85vh] !min-h-0 !p-0"}
        id={modalId}
      >
        <div className="flex flex-1 min-h-0 w-full flex-col overflow-hidden">
          {/* Özel başlık: çarpı solda, box-shadow */}
          {mapModal.isOpen && (
            <div
              className="flex flex-shrink-0 items-center gap-3 bg-white p-6 relative z-10"
              style={{ boxShadow: "0 4px 16px 2px rgba(20, 20, 20, 0.10)" }}
            >
              <h2 className="text-lg font-semibold text-foreground truncate flex-1 min-w-0 text-center">
                {mapTitle}
              </h2>

              <button
                type="button"
                onClick={() => mapModal.close()}
                className="flex w-5 h-5 items-center justify-center text-foreground"
                aria-label="Close modal"
              >
                <FontAwesomeIcon
                  icon={byPrefixAndName.fak["x-mark-custom"]}
                  className="h-5 w-5 text-[20px] cursor-pointer"
                />
              </button>
            </div>
          )}
          <div
            className={clsx(
              "flex min-h-0 w-full overflow-hidden",
              modalMapFullHeight ? "h-[100vh]" : "flex-1",
            )}
          >
          {mapModal.isOpen && useInteractiveMap && mapConfig && (
            <HotelSearchMapView
              initialBoundingBox={mapConfig.initialBoundingBox}
              checkinDate={mapConfig.checkinDate}
              checkoutDate={mapConfig.checkoutDate}
              occupancies={mapConfig.occupancies || []}
              occupancyText={mapConfig.occupancyText || ""}
              userFilterInput={mapConfig.userFilterInput}
              preferences={mapConfig.preferences || {}}
              destination={mapConfig.destination}
              mapTitle={mapTitle}
              onSelectHotel={mapConfig.onSelectHotel}
              filters={mapConfig.filters}
              filterOptions={mapConfig.filterOptions}
              onFilterChange={mapConfig.onFilterChange}
              order={mapConfig.order}
              onSortChange={mapConfig.onSortChange}
              discountRate={mapConfig.discountRate}
              onCampaignChange={mapConfig.onCampaignChange}
              filterLoading={mapConfig.filterLoading}
              itemListId={mapConfig.wishlistItemListId}
              itemListName={mapConfig.wishlistItemListName}
              passengers={mapConfig.wishlistPassengers}
            />
          )}
          {mapModal.isOpen && !useInteractiveMap && iframeSrc && (
            <GoogleMapCard
              src={iframeSrc}
              height="100%"
              title={mapTitle}
              className="flex-1"
              classNameIframe="rounded-none"
            />
          )}
          </div>
        </div>
      </Modal>
    </>
  );
}